/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { Action, ActionCreator, createAction, props } from '@ngrx/store';
import { Resource, ResourceUri } from '@ngxp/rest';
import { CreateResourceData, ListItemResource, StateInfo } from '../resource/resource.model';
import { ListResource } from '../resource/resource.util';
import { ApiError, HttpError } from '../tech.model';

export const EMPTY_ACTION: Action = {} as Action;

export interface TypedActionCreatorWithProps<T> extends ActionCreator<string, (props: T) => T & Action<string>> {}
export interface TypedActionCreator extends ActionCreator<string, () => Action<string>> {}

export interface ApiErrorAction {
  //TODO rename ApiErrorProps
  apiError: ApiError;
}

export interface ResourceUriProps {
  resourceUri: ResourceUri;
}

export interface LoadResourceSuccessProps {
  resource: Resource;
}

export interface LoadListResourceSuccessProps {
  listResource: ListResource;
}

export interface CreateListItemProps {
  payload: CreateResourceData;
}

export interface CreateListItemSuccessProps {
  resource: ListItemResource;
}

export interface CreateListItemFailureProps {
  error: HttpError;
}

export interface LoadResourceFailureProps {
  error: HttpError;
}
export interface ResourceActions {
  loadAction: TypedActionCreatorWithProps<ResourceUriProps>;
  loadFailureAction: TypedActionCreatorWithProps<LoadResourceFailureProps>;
  clearAction: TypedActionCreator;
  reloadAction: TypedActionCreator;
}

export interface SingleResourceLoadActions extends ResourceActions {
  loadSuccessAction: TypedActionCreatorWithProps<LoadResourceSuccessProps>;
}

export interface ListResourceLoadActions extends ResourceActions {
  loadListSuccessAction: TypedActionCreatorWithProps<LoadListResourceSuccessProps>;
  createListItemAction: TypedActionCreatorWithProps<CreateListItemProps>;
  createListItemSuccessAction: TypedActionCreatorWithProps<CreateListItemSuccessProps>;
  createListItemFailureAction: TypedActionCreatorWithProps<CreateListItemFailureProps>;
}

export function createSingleResourceActions(stateInfo: StateInfo): SingleResourceLoadActions {
  const actions: ResourceActions = createResourceActions(stateInfo.name, `${stateInfo.path} Resource`);
  return { ...actions, loadSuccessAction: createLoadSuccessAction(stateInfo) };
}

export function createLoadSuccessAction(stateInfo: StateInfo): TypedActionCreatorWithProps<LoadResourceSuccessProps> {
  return createAction(
    createActionType(stateInfo.name, `Load ${stateInfo.path} Resource Success`),
    props<LoadResourceSuccessProps>(),
  );
}

function createResourceActions(stateName: string, message: string): ResourceActions {
  const loadAction: TypedActionCreatorWithProps<ResourceUriProps> = createAction(
    createActionType(stateName, `Load ${message}`),
    props<ResourceUriProps>(),
  );
  const loadFailureAction: TypedActionCreatorWithProps<LoadResourceFailureProps> = createAction(
    createActionType(stateName, `Load ${message} Failure`),
    props<LoadResourceFailureProps>(),
  );
  const clearAction: TypedActionCreator = createAction(createActionType(stateName, `Clear ${message}`));
  const reloadAction: TypedActionCreator = createAction(createActionType(stateName, `Reload ${message}`));
  return { loadAction, loadFailureAction, clearAction, reloadAction };
}

function createActionType(stateName: string, message: string): string {
  return `[${stateName}] ${message}`;
}

export const serverErrorAction: TypedActionCreatorWithProps<ApiErrorAction> = createAction(
  '[Http] Server Error',
  props<ApiErrorAction>(),
);

export function createListResourceActions(stateInfo: StateInfo): ListResourceLoadActions {
  const actions: ResourceActions = createResourceActions(stateInfo.name, `${stateInfo.path} ListResource`);
  return {
    ...actions,
    loadListSuccessAction: createLoadListSuccessAction(stateInfo),
    createListItemAction: createCreateListItemAction(stateInfo),
    createListItemSuccessAction: createCreateListItemSuccessAction(stateInfo),
    createListItemFailureAction: createCreateListItemFailureAction(stateInfo),
  };
}

function createLoadListSuccessAction(stateInfo: StateInfo): TypedActionCreatorWithProps<LoadListResourceSuccessProps> {
  return createAction(
    createActionType(stateInfo.name, `Load ${stateInfo.path} ListResource Success`),
    props<LoadListResourceSuccessProps>(),
  );
}

function createCreateListItemAction(stateInfo: StateInfo): TypedActionCreatorWithProps<CreateListItemProps> {
  return createAction(createActionType(stateInfo.name, `Create ${stateInfo.path} List Item`), props<CreateListItemProps>());
}

function createCreateListItemSuccessAction(stateInfo: StateInfo): TypedActionCreatorWithProps<CreateListItemSuccessProps> {
  return createAction(
    createActionType(stateInfo.name, `Create ${stateInfo.path} List Item Success`),
    props<CreateListItemSuccessProps>(),
  );
}

function createCreateListItemFailureAction(stateInfo: StateInfo): TypedActionCreatorWithProps<CreateListItemFailureProps> {
  return createAction(
    createActionType(stateInfo.name, `Create ${stateInfo.path} List Item Failure`),
    props<CreateListItemFailureProps>(),
  );
}
